import type { JobTypeVersion } from '../api/client';
import type { FieldContent, JobTypeVersionContent } from '@capture/shared';
import { flattenToLegacyContent, migrateContentToV3 } from '@capture/shared';
import { FieldRow } from '../fields/FieldRow';
import { StageStrip } from '../fields/StageStrip';
import './HistoryTab.css';

interface ViewVersionModalProps {
  version: JobTypeVersion;
  onClose: () => void;
}

/**
 * R-57 (2026-05-14) — read-only modal that previews an older version's
 * fields. Three things were wrong before this fix:
 *
 *   1. No column header — the rows showed Name / Label / Type / Scope /
 *      Stages / Errors with no labels, so operators had to guess which
 *      cell meant what.
 *   2. FieldRow rendered with cursor:pointer because it's an editable
 *      list item by default, but in the modal there's nothing to click
 *      through to. Now we pass variant="readonly" so the row drops the
 *      affordance.
 *   3. The StageStrip wrapped awkwardly inside the constrained modal
 *      width. Its outer wrapper now overflows horizontally so all 11
 *      stage icons stay on a single row that the operator can scroll.
 *
 * The `--field-row-columns` custom property is scoped to .field-list-pane
 * elsewhere; we apply it locally on the table wrapper so the FieldRow's
 * grid template resolves correctly inside the modal too.
 */
export function ViewVersionModal({ version, onClose }: ViewVersionModalProps) {
  // F-037 — `version.content` is raw stored content and can be V3 (per-form),
  // where `.fields` lives under forms[]. Flatten to the legacy view before
  // reading top-level `.fields`. Idempotent for V2.
  const legacyContent = flattenToLegacyContent(
    migrateContentToV3(version.content as unknown as JobTypeVersionContent),
  );
  return (
    <div className="view-version-modal" role="dialog" aria-modal="true">
      <header>
        <h2>v{version.versionNumber} — {version.state}</h2>
        {version.publishNote && <p className="view-version-modal__note">{version.publishNote}</p>}
        <button type="button" onClick={onClose} aria-label="Close">✕</button>
      </header>
      <div className="view-version-modal__body">
        <div className="view-version-modal__columns" role="row" aria-label="Field columns">
          <span className="col-name">Name</span>
          <span className="col-label">Label</span>
          <span className="col-type">Type</span>
          <span className="col-scope">Scope</span>
          <span className="col-stages">Stages</span>
          <span className="col-errors" aria-label="Errors">Err</span>
        </div>
        <ul className="view-version-modal__rows">
          {legacyContent.fields.map((f: FieldContent) => (
            <li key={f.fieldDefinitionId} className="view-version-modal__row">
              <FieldRow
                field={f}
                selected={false}
                errorCount={0}
                onSelect={() => { /* read-only — no nav target */ }}
                variant="readonly"
              />
              <div className="view-version-modal__stage-strip">
                <StageStrip stageMap={f.stageMap} />
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
